// cSpell:ignore Silae mtpart cumul cumulstockvar
import type { SilaeCondition, AdvancedVariableParams } from "@/types/silae";

import { bold, italic, muted } from "./markup";
import { formatOperator } from "./operators";
import {
  formatCumulBulletins,
  formatCumulStockVar,
  formatQuestionnaireValue,
  formatQuestionnaireVariable,
  formatSilaeDateExpression,
} from "./sources";
import type { FormatterContext } from "./types";
import {
  appendUnit,
  formatPredefinedVariableLabel,
  resolveOptionLabel,
  resolveVariableLabel,
} from "./variables";

/**
 * Formate une variable SILAE de type `function` avec son préfixe « Fonction »
 * et chaque paramètre affiché comme « <m>Libellé :</m> <b>valeur</b> ».
 * Ex : « <m>Fonction :</m> <i>Affectation analytique</i> (<m>Renseigner le nom de l'axe :</m> <b>Axe_test</b>) »
 */
function formatFunctionVariableLabel(
  friendlyName: string,
  paramDefs: AdvancedVariableParams[],
  userParams: AdvancedVariableParams[],
  ctx: FormatterContext,
): string {
  const prefix = ctx.t(
    "step5Validation.formatters.conditions.functionPrefix",
    "Fonction",
  );

  const parts = paramDefs
    .filter((def) => !def.isOutputParam)
    .map((def) => {
      let rawValue: string | null = null;
      if (def.isFixed && def.fixedValue) {
        rawValue = def.fixedValue;
      } else {
        const user = userParams.find((p) => p.id === def.id);
        if (user?.value) rawValue = user.value;
      }
      if (!rawValue) return null;

      const displayValue =
        def.type === "date" || def.type === "dynamicDate"
          ? formatSilaeDateExpression(rawValue)
          : rawValue;

      const paramLabel = def.label?.trim() || def.id;
      return `${muted(paramLabel + " :")} ${bold(displayValue)}`;
    })
    .filter((s): s is string => s !== null);

  const paramsSection = parts.length > 0 ? ` (${parts.join(" · ")})` : "";

  return `${muted(prefix + " :")} ${italic(friendlyName)}${paramsSection}`;
}

function applyConditionPercentage(
  base: string,
  percentage: string | undefined,
  ctx: FormatterContext,
): string {
  const value = (percentage ?? "100").trim();
  if (!value || value === "100") return base;
  return ctx.t("step5Validation.formatters.connectives.percentageOf", {
    percent: value,
    base,
    defaultValue: "{{percent}} % de {{base}}",
  });
}

export function formatCondition(
  condition: SilaeCondition,
  ctx: FormatterContext,
): string {
  const conditionType = condition.type as string;

  if (conditionType && conditionType.startsWith("VAR_")) {
    if (!condition.silaeVariable) {
      return ctx.t("step5Validation.conditions.notDefined");
    }
    const displayName = formatPredefinedVariableLabel(conditionType, ctx);
    const variable = ctx.findVariable(condition.silaeVariable);
    const valueRaw =
      condition.conditionValue ??
      ctx.t(
        "step5Validation.formatters.fallbacks.unsetValue",
        "valeur non définie",
      );
    const valueResolved =
      variable?.type === "selection"
        ? resolveOptionLabel(variable.options, condition.conditionValue ?? "")
        : valueRaw;
    const value = appendUnit(valueResolved, variable?.unit);
    const left = applyConditionPercentage(
      italic(displayName),
      condition.conditionPercentage,
      ctx,
    );
    const operator = formatOperator(condition.conditionOperator, ctx);
    return `${left} ${operator} ${bold(value)}`;
  }

  switch (conditionType) {
    case "silae_variable": {
      if (!condition.silaeVariable) {
        return ctx.t("step5Validation.conditions.notDefined");
      }

      const variable = ctx.findVariable(condition.silaeVariable);

      const friendlyName =
        variable?.code === "mtpart.xxxx" && condition.mtpartValue
          ? condition.silaeVariable.replace(
              /\.xxxx/,
              `.${condition.mtpartValue}`,
            )
          : resolveVariableLabel(variable, condition.silaeVariable);

      const isFunction = variable?.type === "function";
      const isNumeric = variable?.type === "int" || variable?.type === "double";

      const resolvedLabel =
        isFunction && variable.parameters
          ? formatFunctionVariableLabel(
              friendlyName,
              variable.parameters,
              condition.functionParameters ?? [],
              ctx,
            )
          : friendlyName;

      // Pour les fonctions le markup est déjà intégralement construit dans resolvedLabel.
      const left = isFunction
        ? resolvedLabel
        : isNumeric
          ? applyConditionPercentage(
              italic(resolvedLabel),
              condition.conditionPercentage,
              ctx,
            )
          : italic(resolvedLabel);

      const valueRaw =
        condition.conditionValue ??
        ctx.t(
          "step5Validation.formatters.fallbacks.unsetValue",
          "valeur non définie",
        );
      const valueResolved =
        variable?.type === "selection"
          ? resolveOptionLabel(variable.options, condition.conditionValue ?? "")
          : valueRaw;
      const value = appendUnit(valueResolved, variable?.unit);

      const operator = formatOperator(condition.conditionOperator, ctx);
      return `${left} ${operator} ${bold(value)}`;
    }

    case "custom": {
      // Expression SILAE personnalisée saisie librement par l'utilisateur :
      // on l'affiche telle quelle, sans tenter d'interpréter ses parties.
      const expression = condition.conditionValue?.trim();
      if (!expression) {
        return ctx.t("step5Validation.conditions.notDefined");
      }
      const label = ctx.t(
        "step5Validation.formatters.conditions.customExpression",
        "Expression personnalisée",
      );
      return `${italic(label)} : ${bold(expression)}`;
    }

    case "cumulstockvar": {
      if (
        condition.cumulStockVarName &&
        condition.cumulStockVarDateDebut &&
        condition.cumulStockVarDateFin
      ) {
        const left = formatCumulStockVar(
          condition.cumulStockVarName,
          condition.cumulStockVarDateDebut,
          condition.cumulStockVarDateFin,
          condition.cumulStockVarPercentage,
          condition.cumulStockVarEnableAdditional,
          condition.cumulStockVarAdditionalOperator,
          condition.cumulStockVarAdditionalValue,
          ctx,
          condition.cumulStockVarPeriod,
        );
        const operator = formatOperator(condition.conditionOperator, ctx);
        const value = condition.conditionValue || "0";
        return `${italic(left)} ${operator} ${bold(value)}`;
      }
      return ctx.t("step5Validation.conditions.notConfigured");
    }

    case "cumul_bulletins": {
      if (condition.cumulBulletinsPeriod && condition.cumulBulletinsType) {
        const left = formatCumulBulletins(
          condition.cumulBulletinsPeriod,
          condition.cumulBulletinsType,
          condition.cumulBulletinsDateDebut,
          condition.cumulBulletinsDateFin,
          condition.cumulBulletinsDivisor,
          ctx,
        );
        const operator = formatOperator(condition.conditionOperator, ctx);
        const value = condition.conditionValue || "0";
        return `${italic(left)} ${operator} ${bold(value)}`;
      }
      return ctx.t("step5Validation.conditions.notConfigured");
    }

    case "questionnaire_variable": {
      if (
        condition.questionnaireVariableType &&
        condition.questionnaireVariableIndex
      ) {
        const left = formatQuestionnaireVariable(
          condition.questionnaireVariableType,
          condition.questionnaireVariableIndex,
          condition.questionnaireVariableLabel,
          condition.questionnaireVariablePercentage,
          ctx,
        );
        const operator = formatOperator(
          condition.questionnaireVariableOperator,
          ctx,
        );
        const value = formatQuestionnaireValue(
          condition.questionnaireVariableType,
          condition.questionnaireVariableValue,
          ctx,
        );
        return `${italic(left)} ${operator} ${bold(value)}`;
      }
      return ctx.t("step5Validation.conditions.questionnaireNotConfigured");
    }

    default:
      return ctx.t("step5Validation.conditions.notDefined");
  }
}
